import { useCallback, useEffect, useState } from "react";
import { BookOpenText, Pencil, Plus, Save, X } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { ReferentialImport } from "../components/ReferentialImport";
import { getAccountTypeRoute, getStoredAccountType } from "../auth";
import {
  fetchAffectionCodes,
  saveAffectionCode,
  type AffectionCode,
  type AffectionCodePayload,
  type AffectionCodesResponse,
} from "../api/referentialsApi";

type Draft = AffectionCodePayload & { code: string };

const EMPTY: Draft = { code: "", label: "", chapter: null, active: true };

function payloadOf(code: AffectionCode, active = code.active): AffectionCodePayload {
  return { label: code.label, chapter: code.chapter, active };
}

/**
 * Référentiel CIM-10 (OMS) des codes d'affection que le médecin associe à chaque patient d'une facture.
 * Admin et Sinistre ajoutent, corrigent ou désactivent un code ; l'Audit consulte.
 */
export function AffectionCodesPage() {
  const { showToast } = useToast();
  const [data, setData] = useState<AffectionCodesResponse | null>(null);
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [chapter, setChapter] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Recherche lancée 300 ms après la frappe.
  useEffect(() => {
    const timer = setTimeout(() => {
      setTerm(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(() => {
    fetchAffectionCodes({ q: term, chapter, active: status, page })
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."));
  }, [term, chapter, status, page]);

  useEffect(load, [load]);

  const canEdit = data?.can_edit ?? false;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1;
  const chapterLabel = (value: string | null) => data?.chapters.find((c) => c.chapter === value);

  const closeForm = () => {
    setDraft(null);
    setEditing(null);
  };

  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      showToast(message);
      load();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!draft || !draft.label.trim() || (!editing && !draft.code.trim())) return;
    const { code, ...payload } = draft;
    const ok = await run(
      () => saveAffectionCode(editing ? payload : { ...payload, code: code.trim() }, editing ?? undefined),
      editing ? "Code d'affection enregistré." : "Code d'affection ajouté.",
    );
    if (ok) closeForm();
  };

  const statusBadge = (active: boolean) => `rounded-full px-2 py-0.5 text-xs ${active ? "bg-green-100 text-green-800" : "bg-card-muted text-muted"}`;

  return (
    <div className="flex flex-col gap-5">
      <Breadcrumbs homeHref={getAccountTypeRoute(getStoredAccountType() ?? "admin")} items={[{ label: "Référentiels" }, { label: "Codes d'affection" }]} />

      <section className="theme-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              <BookOpenText size={20} className="text-primary" />
              Codes d'affection (CIM-10)
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-muted">
              Classification internationale des maladies (OMS), proposée au médecin pour coder les affections de chaque
              patient d'une facture. Un code désactivé n'est plus proposé, mais reste sur les factures déjà codées.
              {canEdit ? "" : " Consultation seule."}
            </p>
          </div>
          {canEdit ? (
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setDraft(EMPTY);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground"
            >
              <Plus size={15} />
              Nouveau code
            </button>
          ) : null}
        </div>
        {canEdit ? (
          <div className="mt-4">
            <ReferentialImport kind="icd10" columns="Code, Libellé (obligatoires), Chapitre, Actif" onImported={load} />
          </div>
        ) : null}
        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      </section>

      {draft ? (
        <section className="theme-card p-5">
          <h2 className="mb-3 text-base font-semibold text-foreground">{editing ? `Modifier ${editing}` : "Nouveau code d'affection"}</h2>
          <div className="grid gap-3 md:grid-cols-4">
            <FormField label="Code" required hint="ex. B50 ou B50.9">
              <input
                value={draft.code}
                disabled={Boolean(editing)}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                className={inputClass}
              />
            </FormField>
            <FormField label="Libellé" required className="md:col-span-2">
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className={inputClass} />
            </FormField>
            <FormField label="Chapitre" hint="Déduit du code si vide.">
              <select value={draft.chapter ?? ""} onChange={(e) => setDraft({ ...draft, chapter: e.target.value || null })} className={selectClass}>
                <option value="">— Automatique —</option>
                {(data?.chapters ?? []).map((c) => (
                  <option key={c.chapter} value={c.chapter}>{c.range} · {c.label}</option>
                ))}
              </select>
            </FormField>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
            Actif (proposé à la codification)
          </label>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              disabled={busy || !draft.label.trim() || (!editing && !draft.code.trim())}
              onClick={() => void handleSave()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
            >
              <Save size={15} />
              Enregistrer
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-foreground hover:bg-accent/20 hover:border-accent"
            >
              <X size={15} />
              Annuler
            </button>
          </div>
        </section>
      ) : null}

      <section className="theme-card p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Code (B50, B50.9) ou mot (paludisme)…"
            className={`${inputClass} max-w-sm`}
          />
          <select
            value={chapter}
            onChange={(e) => {
              setChapter(e.target.value);
              setPage(1);
            }}
            className={`${selectClass} sm:max-w-xs`}
          >
            <option value="">Tous les chapitres</option>
            {(data?.chapters ?? []).map((c) => (
              <option key={c.chapter} value={c.chapter}>{c.range} · {c.label}</option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className={`${selectClass} sm:max-w-[13rem]`}
          >
            <option value="">Actifs et inactifs</option>
            <option value="1">Actifs</option>
            <option value="0">Inactifs</option>
          </select>
          <span className="text-sm text-muted">{data ? `${data.total.toLocaleString("fr-FR")} code(s)` : ""}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-muted">
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Libellé</th>
                <th className="px-3 py-2">Chapitre</th>
                <th className="px-3 py-2 text-right" title="Patients de factures codés avec cette affection">Utilisé</th>
                <th className="px-3 py-2">Statut</th>
                {canEdit ? <th className="px-3 py-2" /> : null}
              </tr>
            </thead>
            <tbody>
              {(data?.codes ?? []).map((code) => {
                const info = chapterLabel(code.chapter);
                return (
                  <tr key={code.code} className={`border-b border-border ${code.active ? "" : "opacity-60"}`}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-primary">
                      {code.code}
                      {code.who_list ? <span className="ml-1 rounded bg-card-muted px-1 text-[10px] font-normal text-muted" title="Liste internationale OMS">OMS</span> : null}
                    </td>
                    <td className="px-3 py-2 text-foreground">{code.label}</td>
                    <td className="px-3 py-2 text-xs text-muted" title={info?.label}>{info ? info.range : code.chapter ?? "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted">{code.used}</td>
                    <td className="px-3 py-2">
                      {canEdit ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            void run(
                              () => saveAffectionCode(payloadOf(code, !code.active), code.code),
                              code.active ? `${code.code} désactivé.` : `${code.code} réactivé.`,
                            )
                          }
                          title={code.active ? "Désactiver" : "Réactiver"}
                          className={statusBadge(code.active)}
                        >
                          {code.active ? "Actif" : "Inactif"}
                        </button>
                      ) : (
                        <span className={statusBadge(code.active)}>{code.active ? "Actif" : "Inactif"}</span>
                      )}
                    </td>
                    {canEdit ? (
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(code.code);
                            setDraft({ code: code.code, ...payloadOf(code) });
                          }}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-primary hover:bg-accent/20"
                        >
                          <Pencil size={13} />
                          Modifier
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
              {data && data.codes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted">Aucun code ne correspond.</td>
                </tr>
              ) : null}
              {!data ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted">Chargement…</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {pages > 1 ? (
          <div className="mt-3 flex items-center justify-end gap-2 text-sm">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="rounded-lg border border-border px-3 py-1 text-foreground hover:bg-accent/20 hover:border-accent disabled:opacity-50"
            >
              Précédent
            </button>
            <span className="text-muted">Page {page} / {pages.toLocaleString("fr-FR")}</span>
            <button
              type="button"
              disabled={page >= pages}
              onClick={() => setPage(page + 1)}
              className="rounded-lg border border-border px-3 py-1 text-foreground hover:bg-accent/20 hover:border-accent disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
